import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import { faRectangleList } from "@fortawesome/free-regular-svg-icons";
import { pageProps } from "../../../../helpers/pageProps";
import MyModal from "../../../../Common/MyModal";
import Timeline from "../../../../ProjectsOverview/Processes/Timeline";
import WorkflowLogs from "../../../../ProjectsOverview/Processes/WorkflowLogs";
import { proxyAPI } from "../../../../proxyAPI";

interface StartButtonProps {
  isStartDisabled: boolean;
  starting: boolean;
  onStart: () => void;
  processGUID: string;
  processID: string;
  projectID: string | number;
}

function ProcessTimelineModalContent({
  processID,
  projectID,
}: {
  processID: string;
  projectID: string | number;
}) {
  const [process, setProcess] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [processes, setProcesses] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [showLogs, setShowLogs] = useState<{
    GUID: string | null;
    runtimeID: number | null;
  }>({ GUID: null, runtimeID: null });

  const loader = async (
    startAt: Date,
    endAt: Date,
    status: string,
    offset: number,
    limit = 10
  ) => {
    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/process?id=${processID}&start_at=${startAt.toISOString()}&end_at=${endAt.toISOString()}&offset=${offset}&status=${status}&limit=${limit}`,
    });

    if (data.process) {
      setTotal(data.total);
      setProcesses(data.runtimes);
      setProcess(data.process);
      setProject(data.process.Project);
    }
  };

  return (
    <>
      <Timeline
        title={
          <>
            <span className="text-white">Workflows</span>
            <button
              type="button"
              title="Show process logs"
              className="btn btn-link ms-3 p-0 text-white"
              onClick={() =>
                setShowLogs({ GUID: process?.GUID, runtimeID: null })
              }
              disabled={!process?.GUID}
            >
              <FontAwesomeIcon icon={faRectangleList} />
            </button>
          </>
        }
        loader={loader}
        processes={processes}
        total={total}
        initialStatus="active"
        useLocalTimeFilters
        liveMonitoringDisabled={process ? !process.IsActive : true}
        defaultLiveMonitoring
        // eslint-disable-next-line react/no-unstable-nested-components
        link={(runtime: any) =>
          runtime.Workflow ? (
            <td>
              <div className="small text-secondary bg-transparent">
                Workflow
              </div>
              <a
                title="Show workflow in builder"
                target="_blank"
                href={`${(pageProps as any).baseUrl}/workflows/builder/${
                  project?.ID
                }/workflow/${runtime.Workflow.UUID}`}
                rel="noreferrer"
                className="bg-transparent"
              >
                {runtime.Workflow.Label}
              </a>
              <button
                type="button"
                title="Show workflow logs"
                className="btn btn-link align-baseline p-0 ms-3 bg-transparent"
                onClick={() =>
                  setShowLogs({ GUID: process?.GUID, runtimeID: runtime.ID })
                }
                disabled={!process?.GUID}
              >
                <FontAwesomeIcon icon={faRectangleList} />
              </button>
            </td>
          ) : (
            <td>
              <div className="small text-secondary bg-transparent">Project</div>
              <a
                target="_blank"
                href={`${(pageProps as any).baseUrl}/workflows/builder/${
                  project?.ID
                }`}
                rel="noreferrer"
                className="bg-transparent"
              >
                {project?.Label}
              </a>
            </td>
          )
        }
      />
      <div className="text-end mt-3">
        <a
          href={`${
            (pageProps as any).baseUrl
          }/workflows/reporting/processes/${projectID}/process/${processID}`}
          target="_blank"
          rel="noreferrer"
        >
          Open full process page
        </a>
      </div>
      <WorkflowLogs
        {...showLogs}
        handleClose={() => setShowLogs({ GUID: null, runtimeID: null })}
      />
    </>
  );
}

export default function StartButton({
  isStartDisabled,
  starting,
  onStart,
  processGUID,
  processID,
  projectID,
}: StartButtonProps) {
  return (
    <>
      <button
        type="button"
        className="btn btn-colored form-control"
        onClick={onStart}
        disabled={isStartDisabled || starting}
      >
        Start
        {starting ? (
          <span className="ms-2">
            <FontAwesomeIcon icon={faRotate} spin />
          </span>
        ) : (
          ""
        )}
      </button>

      {isStartDisabled && (
        <div className="text-danger text-center">
          There are empty values in required fields
        </div>
      )}

      {processGUID !== "" ? (
        <div className="my-3">
          Process{" "}
          <MyModal
            heading={`Process ${processGUID}`}
            size="xxl"
            label={processGUID}
            buttonClassName="btn-link align-baseline"
          >
            <ProcessTimelineModalContent
              processID={processID}
              projectID={projectID}
            />
          </MyModal>{" "}
          started.
        </div>
      ) : (
        ""
      )}

      <div className="my-3">
        <a
          href={`${
            (pageProps as any).baseUrl
          }/workflows/reporting/processes/${projectID}`}
          target="_blank"
          rel="noreferrer"
        >
          See all
        </a>{" "}
        processes from this project.
      </div>
    </>
  );
}
